import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Loader2, Camera, Save, Mail, Calendar, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import Avatar from "../studio/Avatar";
import {
  adminFetchMe,
  adminUpdateMe,
  adminUploadAvatar,
} from "../../lib/studioApi";

function safeDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return "—";
  }
}

/**
 * AdminAccountModal — modal-based profile + settings for the admin (Paul).
 *
 * Props:
 *  - open: boolean
 *  - mode: "profile" | "settings"
 *  - onOpenChange: (open) => void
 *  - focusAvatar: open settings with the avatar file picker auto-triggered
 *  - onUpdate: (newAdminProfile) => void
 *  - profile: current admin profile from parent
 */
export default function AdminAccountModal({
  open,
  mode = "profile",
  onOpenChange,
  focusAvatar = false,
  onUpdate,
  profile,
}) {
  const [view, setView] = useState(mode);
  const [me, setMe] = useState(profile);
  const [artistName, setArtistName] = useState(profile?.artistName || "");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open && (mode === "settings" || focusAvatar) && focusAvatar) {
      const t = setTimeout(() => fileInputRef.current?.click(), 250);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open, mode, focusAvatar]);

  const refresh = async () => {
    try {
      const fresh = await adminFetchMe();
      setMe(fresh);
      if (onUpdate) onUpdate(fresh);
    } catch {
      /* ignore */
    }
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image too large (max 10MB)");
      return;
    }
    if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) {
      toast.error("Only JPG, PNG, or WEBP allowed");
      return;
    }
    setUploading(true);
    try {
      const updated = await adminUploadAvatar(file);
      setMe(updated);
      if (onUpdate) onUpdate(updated);
      toast.success("Profile picture updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const saveName = async () => {
    if (!artistName.trim()) {
      toast.error("Display name cannot be empty");
      return;
    }
    setSaving(true);
    try {
      const updated = await adminUpdateMe({ artistName: artistName.trim() });
      setMe(updated);
      if (onUpdate) onUpdate(updated);
      toast.success("Saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!me) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg bg-[#10162a] border-white/10 text-white"
        data-testid={`admin-account-modal-${view}`}
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-extrabold text-white flex items-center gap-2">
            {view === "settings" && (
              <button
                type="button"
                onClick={() => setView("profile")}
                className="text-white/50 hover:text-paul-yellow"
                data-testid="admin-account-back-to-profile"
                aria-label="Back to profile"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            {view === "profile" ? "Your Profile" : "Account Settings"}
          </DialogTitle>
          <DialogDescription className="text-white/60">
            {view === "profile"
              ? "How you appear to artists across the studio."
              : "Update your display name and profile picture."}
          </DialogDescription>
        </DialogHeader>

        {view === "profile" ? (
          <div className="space-y-5">
            <div className="flex items-start gap-5">
              <Avatar user={me} size="xl" testIdPrefix="admin-profile-avatar" />
              <div className="flex-1 min-w-0">
                <div className="text-xl font-extrabold text-white" data-testid="admin-profile-name">
                  {me.artistName || me.name}
                </div>
                <div className="text-sm text-white/50 mt-1">Admin</div>
                <div className="mt-4 space-y-2 text-sm text-white/70">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-paul-yellow" />
                    <span>Studio Owner</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-paul-yellow" />
                    <span>
                      Last active{" "}
                      <span data-testid="admin-profile-last-seen">{safeDate(me.lastSeenAt)}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <Button
              data-testid="admin-profile-edit-btn"
              onClick={() => setView("settings")}
              className="w-full bg-paul-yellow hover:bg-paul-yellow text-navy font-bold"
              style={{ color: "#0b1535" }}
            >
              Edit profile
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Avatar */}
            <div>
              <label className="text-xs font-semibold text-white/50 uppercase tracking-wider">Profile Picture</label>
              <div className="mt-2 flex items-center gap-4">
                <Avatar user={me} size="xl" testIdPrefix="admin-settings-avatar" />
                <div className="flex flex-col gap-1">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    hidden
                    onChange={onPickFile}
                    data-testid="admin-settings-avatar-file"
                  />
                  <Button
                    data-testid="admin-settings-upload-btn"
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="bg-paul-yellow hover:bg-paul-yellow text-navy font-bold"
                    style={{ color: "#0b1535" }}
                  >
                    {uploading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Camera className="w-4 h-4 mr-2" />
                    )}
                    {me.avatarUrl ? "Change" : "Upload"}
                  </Button>
                  {me.avatarUrl && (
                    <button
                      type="button"
                      data-testid="admin-settings-remove-avatar"
                      onClick={async () => {
                        try {
                          const updated = await adminUpdateMe({ artistName: me.artistName });
                          // backend doesn't expose an explicit "remove avatar" endpoint, so this is a UX nicety only
                          await refresh();
                        } catch {
                          /* ignore */
                        }
                      }}
                      className="text-[11px] text-white/40 hover:text-white/70"
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Name */}
            <div>
              <label className="text-xs font-semibold text-white/50 uppercase tracking-wider">Display Name</label>
              <Input
                data-testid="admin-settings-name"
                value={artistName}
                onChange={(e) => setArtistName(e.target.value)}
                className="mt-1 bg-[#0b0f1e] border-white/10 text-white placeholder:text-white/30 focus-visible:border-paul-yellow/60 focus-visible:ring-paul-yellow/40"
              />
              <p className="text-[11px] text-white/40 mt-1">
                This is what artists see at the top of their DM thread.
              </p>
            </div>

            <Button
              data-testid="admin-settings-save-btn"
              onClick={saveName}
              disabled={saving}
              className="w-full bg-paul-yellow hover:bg-paul-yellow text-navy font-bold"
              style={{ color: "#0b1535" }}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save changes
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
